import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import clsx from 'clsx';
import { FunctionComponent, PropsWithChildren, ReactElement, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MemoryRouter } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import { Banner, CustomCSSProperties, GlobalsContext } from '@vestico/shared';
import '@vestico/shared/src/layouts/globalWrapper.scss';
import resetStyle from '@vestico/shared/src/layouts/reset.module.css';

import { i18n } from '@vestico/i18n';

import { ConfigContext } from '../WidgetEventsManager';
import '../layout/fonts.css';
import style from './MainWindow.module.css';

const MAIN_WINDOW_TRANSITION_DURATION = 300;

export const WidgetRootElementContext = createContext<HTMLElement | null>(null);

export interface MainWindowProps extends PropsWithChildren {
  topBackgroundContent?: ReactElement | ReactElement[];
  bottomBackgroundContent?: ReactElement | ReactElement[];
  leftBackgroundContent?: ReactElement | ReactElement[];
  rightBackgroundContent?: ReactElement | ReactElement[];
  isOpen: boolean;
  bordered: boolean;
  focusWindow?: boolean;
}

export const MainWindow: FunctionComponent<MainWindowProps> = ({
  isOpen,
  topBackgroundContent,
  bottomBackgroundContent,
  leftBackgroundContent,
  rightBackgroundContent,
  children,
  bordered,
  focusWindow,
}) => {
  const { disableDarkMode, isSDK } = useContext(ConfigContext);
  const { stylesConfig, useMobileLayout } = useContext(GlobalsContext);

  const [rootElement, setRootElement] = useState<HTMLElement | null>(null);
  const bodyScrollEnabledRef = useRef<boolean>(true);

  const onRefChange = useCallback((node: HTMLDivElement) => {
    setRootElement(node);
  }, []);

  useEffect(() => {
    const current = rootElement;
    const { current: bodyScrollEnabled } = bodyScrollEnabledRef;
    if (!current) {
      return;
    }
    if (isOpen) {
      if (!bodyScrollEnabled) {
        return;
      }
      bodyScrollEnabledRef.current = false;

      disableBodyScroll(current, {
        allowTouchMove: (el) => {
          let ele: HTMLElement | Element | null = el;
          while (ele && ele !== document.body) {
            if (ele.getAttribute('body-scroll-lock-ignore') !== null) {
              const htmlElement = ele as HTMLElement;
              let maxX = 0;
              let maxY = 0;
              for (let idx = 0; idx < htmlElement.children.length; ++idx) {
                const child = htmlElement.children.item(idx) as HTMLElement;
                if (child) {
                  const boundingRect = child.getBoundingClientRect();
                  maxY = Math.max(boundingRect.bottom, maxY);
                  maxX = Math.max(boundingRect.right, maxX);
                }
              }

              const elementWidth = htmlElement.offsetWidth;
              const elementHeight = htmlElement.offsetHeight;
              return maxX > elementWidth || maxY > elementHeight;
            }
            ele = ele.parentElement;
          }
          return false;
        },
      });
    } else {
      enableBodyScroll(current);
      bodyScrollEnabledRef.current = true;
    }
  }, [isOpen, rootElement]);

  const dir = i18n.dir();
  const transitionDuration = !isSDK ? MAIN_WINDOW_TRANSITION_DURATION : 0;

  const hasVerticalBackground = topBackgroundContent !== undefined || bottomBackgroundContent !== undefined;
  const hasAnyBackground = hasVerticalBackground || leftBackgroundContent !== undefined || rightBackgroundContent !== undefined;

  const content = (
    <>
      <div className={clsx(style.root, { [style.noDark]: disableDarkMode })}>
        {topBackgroundContent ? <div className={clsx(style.topBackgroundContent)}>{topBackgroundContent}</div> : null}
        <div className={clsx(style.center)}>
          {leftBackgroundContent ? <div className={clsx(style.leftBackgroundContent)}>{leftBackgroundContent}</div> : null}
          <div
            className={clsx(
              style.outer,
              'vestico-animation-content',
              { [style.hasLeftRightPadding]: hasAnyBackground },
              { [style.hasVerticalPadding]: hasVerticalBackground },
              { [style.bordered]: bordered }
            )}
          >
            <div className={clsx(style.border, { [style.bordered]: bordered })}>
              <div className={clsx(style.container)}>{children}</div>
            </div>
          </div>
          {rightBackgroundContent ? <div className={clsx(style.rightBackgroundContent)}>{rightBackgroundContent}</div> : null}
        </div>
        {bottomBackgroundContent ? <div className={clsx(style.bottomBackgroundContent)}>{bottomBackgroundContent}</div> : null}
      </div>
      {useMobileLayout ? null : <Banner />}
    </>
  );

  useEffect(() => {
    if (focusWindow) {
      const firstElement = rootElement?.querySelector('input') ?? rootElement?.querySelector('button');
      firstElement?.focus();
    }
  }, [focusWindow, rootElement]);

  return (
    <MemoryRouter>
      <div
        className={clsx(resetStyle.root)}
        style={{ '--main-window-transition-duration': `${transitionDuration}ms` } as CustomCSSProperties}
        dir={dir}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
      >
        <CSSTransition in={isOpen} timeout={transitionDuration} classNames={useMobileLayout ? 'slide-up' : 'fade-scale'} unmountOnExit appear>
          <div className={clsx(style.background)} ref={onRefChange} style={stylesConfig.getCustomizedStyles()}>
            <WidgetRootElementContext.Provider value={rootElement}>{content}</WidgetRootElementContext.Provider>
          </div>
        </CSSTransition>
      </div>
    </MemoryRouter>
  );
};
